// Global Instructions Rule Applied!
//
// Configuration → Deal Documents admin models: the document categories, the
// document types they own, and the required-document rules that reference
// those types.
//
// Shape mirrors public.view__document_config_admin. Categories own types
// (cfg.document_types.document_category_id), so a deal file's category is
// always derived from its type — there is no category column on
// public.file_urls_deals and therefore no model for one here.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

export const DocumentCategoryAdminRowSchema = z
  .object({
    document_categories__id: z.string(),
    document_categories__label: z.string().nullish(),
    document_categories__sort_order: z.number().nullish(),
    document_categories__active: z.boolean().nullish(),
    // How many live document types sit in this category — what the delete
    // guard counts, surfaced so an admin sees why a delete is refused.
    document_categories__type_count: z.number().nullish()
  })
  .strict()

export type DocumentCategoryAdminRow = z.infer<typeof DocumentCategoryAdminRowSchema>

export function validateDocumentCategoryAdminRow(
  data: unknown,
  options?: { isArray?: boolean }
): DocumentCategoryAdminRow | DocumentCategoryAdminRow[] {
  try {
    return validateWithZodSchema(
      DocumentCategoryAdminRowSchema,
      data,
      'document category admin row',
      options
    ) as DocumentCategoryAdminRow | DocumentCategoryAdminRow[]
  } catch (e) {
    throw new Error(`Document category validation failed: ${e}`)
  }
}

export const DocumentTypeAdminRowSchema = z
  .object({
    document_types__id: z.string(),
    document_types__label: z.string().nullish(),
    document_types__sort_order: z.number().nullish(),
    document_types__active: z.boolean().nullish(),
    document_category_id: z.string().nullish(),
    document_categories__label: z.string().nullish(),
    document_types__file_count: z.number().nullish(),
    document_types__requirement_count: z.number().nullish()
  })
  .strict()

export type DocumentTypeAdminRow = z.infer<typeof DocumentTypeAdminRowSchema>

export function validateDocumentTypeAdminRow(
  data: unknown,
  options?: { isArray?: boolean }
): DocumentTypeAdminRow | DocumentTypeAdminRow[] {
  try {
    return validateWithZodSchema(
      DocumentTypeAdminRowSchema,
      data,
      'document type admin row',
      options
    ) as DocumentTypeAdminRow | DocumentTypeAdminRow[]
  } catch (e) {
    throw new Error(`Document type validation failed: ${e}`)
  }
}

export const DocumentRequirementAdminRowSchema = z
  .object({
    deal_document_requirements__id: z.string(),
    deal_document_requirements__name: z.string().nullish(),
    deal_document_requirements__required_lifecycle: z.string().nullish(),
    deal_document_requirements__sequence: z.number().nullish(),
    deal_document_requirements__active: z.boolean().nullish(),
    satisfied_by_document_type_ids: z.array(z.string()).nullish(),
    satisfied_by_labels: z.array(z.string()).nullish(),
    // Every scope is a LIST; [] / NULL means "applies to all of them",
    // otherwise the deal must match any entry. Ids stay z.string() (legacy
    // Dataverse ids are not uuids).
    pipeline_ids: z.array(z.string()).nullish(),
    pipelines__names: z.array(z.string()).nullish(),
    deal_type_ids: z.array(z.string()).nullish(),
    deal_type__labels: z.array(z.string()).nullish(),
    security_type_ids: z.array(z.string()).nullish(),
    security_type__labels: z.array(z.string()).nullish(),
    fund_ids: z.array(z.string()).nullish(),
    funds__names: z.array(z.string()).nullish()
  })
  .strict()

export type DocumentRequirementAdminRow = z.infer<typeof DocumentRequirementAdminRowSchema>

export function validateDocumentRequirementAdminRow(
  data: unknown,
  options?: { isArray?: boolean }
): DocumentRequirementAdminRow | DocumentRequirementAdminRow[] {
  try {
    return validateWithZodSchema(
      DocumentRequirementAdminRowSchema,
      data,
      'document requirement admin row',
      options
    ) as DocumentRequirementAdminRow | DocumentRequirementAdminRow[]
  } catch (e) {
    throw new Error(`Document requirement validation failed: ${e}`)
  }
}

export interface DocumentConfigAdminData {
  categories: DocumentCategoryAdminRow[]
  types: DocumentTypeAdminRow[]
  requirements: DocumentRequirementAdminRow[]
}
